import type { types } from "@fraction/shared";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import fraction from "src/api/fraction";
import { cn } from "src/utilities/shadcnUtils";

/**
 * The confirmation itself, with no presentation of its own.
 *
 * Rendered inside a backdrop by `WorkflowConfirmationCard` in the browser and as the whole of a
 * native window on the desktop, so the two presentations can never drift apart.
 */
export function WorkflowConfirmationContent({
  notification,
  onClose,
  onResolved,
  className,
}: {
  notification: types.WorkflowConfirmationRequestNotification;
  /** Omitted in a window, which is dismissed by its own chrome rather than by an X in the content. */
  onClose?: () => void;
  onResolved: () => void;
  className?: string;
}) {
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [freeform, setFreeform] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-fetch the confirmation on open in case the LLM updated options after the
  // initial websocket push, or to surface state from a parallel resolution.
  const [latest, setLatest] = useState<{
    question: string;
    contextSummary?: string;
    proposedAction?: string;
    options: types.WorkflowConfirmationOptionPayload[];
    allowFreeform: boolean;
    status?: string;
  }>({
    question: notification.question,
    contextSummary: notification.contextSummary,
    proposedAction: notification.proposedAction,
    options: notification.options,
    allowFreeform: notification.allowFreeform,
  });

  useEffect(() => {
    let cancelled = false;
    fraction
      .getWorkflowConfirmation(notification.confirmationId)
      .then((c) => {
        if (cancelled) return;
        setLatest({
          question: c.question || notification.question,
          contextSummary: c.contextSummary || notification.contextSummary,
          proposedAction: c.proposedAction || notification.proposedAction,
          options: (c.options || notification.options).map((o) => ({
            id: o.id,
            label: o.label,
            description: o.description,
          })),
          allowFreeform: c.allowFreeform ?? notification.allowFreeform,
          status: c.status,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [notification]);

  const submit = useCallback(
    async (selectedOptionId?: string, freeformText?: string) => {
      setError(null);
      setSubmitting(selectedOptionId || "__freeform");
      try {
        await fraction.resolveWorkflowConfirmation(notification.confirmationId, {
          selectedOptionId,
          freeformText: freeformText?.trim() || undefined,
        });
        onResolved();
      } catch (err: any) {
        setError(err?.response?.body?.error || err?.message || "Failed to resolve");
      } finally {
        setSubmitting(null);
      }
    },
    [notification.confirmationId, onResolved]
  );

  const cancelWorkflow = useCallback(async () => {
    setError(null);
    setSubmitting("__cancel");
    try {
      await fraction.resolveWorkflowConfirmation(notification.confirmationId, { cancel: true });
      onResolved();
    } catch (err: any) {
      setError(err?.response?.body?.error || err?.message || "Failed to cancel");
    } finally {
      setSubmitting(null);
    }
  }, [notification.confirmationId, onResolved]);

  const alreadyResolved = latest.status && latest.status !== "pending";

  return (
    <div className={cn("relative w-full rounded-xl bg-card p-5", className)}>
      {onClose ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-muted-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}

      <p className="text-xs font-medium uppercase tracking-wide text-violet-600">Confirmation needed</p>
      <h2 className="mt-1 text-lg font-semibold text-foreground">{latest.question}</h2>

      {latest.contextSummary ? (
        <div className="mt-3 rounded-md bg-muted/50 p-3 text-sm text-foreground/80">
          {latest.contextSummary}
        </div>
      ) : null}

      {latest.proposedAction ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">Proposed action</p>
          <p className="mt-1 text-sm text-foreground">{latest.proposedAction}</p>
        </div>
      ) : null}

      {alreadyResolved ? (
        <div className="mt-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-700">
          This confirmation has already been resolved.
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-2">
            {latest.options.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={!!submitting}
                onClick={() => submit(option.id)}
                className="flex flex-col items-start gap-0.5 rounded-md border border-border bg-card px-3 py-2 text-left transition-colors hover:border-violet-300 hover:bg-violet-50 disabled:opacity-50"
              >
                <span className="text-sm font-semibold text-foreground">
                  {submitting === option.id ? "Submitting…" : option.label}
                </span>
                {option.description ? (
                  <span className="text-xs text-muted-foreground">{option.description}</span>
                ) : null}
              </button>
            ))}
          </div>

          {latest.allowFreeform ? (
            <div className="mt-4">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="wf-confirm-freeform">
                Or reply freeform
              </label>
              <textarea
                id="wf-confirm-freeform"
                className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                placeholder="Type a custom response…"
                rows={3}
                value={freeform}
                onChange={(e) => setFreeform(e.target.value)}
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={!freeform.trim() || !!submitting}
                  onClick={() => submit(undefined, freeform)}
                  className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting === "__freeform" ? "Submitting…" : "Send freeform reply"}
                </button>
              </div>
            </div>
          ) : null}

          {/* Always-on cancel — aborts the workflow run instead of resuming. */}
          <div className="mt-4 flex justify-between border-t border-border/50 pt-3">
            <button
              type="button"
              disabled={!!submitting}
              onClick={cancelWorkflow}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting === "__cancel" ? "Cancelling…" : "Stop workflow"}
            </button>
            <span className="text-[10px] text-muted-foreground/70">
              Stops the workflow run without continuing to any downstream nodes.
            </span>
          </div>
        </>
      )}

      {error ? <div className="mt-3 text-xs text-red-600">{error}</div> : null}
    </div>
  );
}
